import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import React, { useState, useEffect } from 'react';
import { getUser } from '../../services/authService';
import axiosInstance from '../../utils/axiosInstance';

// 다크 모드 스타일 상수
const darkModeStyles = {
  container: {
    p: { xs: 2, md: 4 },
    background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
    minHeight: '100vh',
    position: 'relative',
  },
  paper: {
    p: { xs: 3, md: 4 },
    background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(8px)',
    mb: 4
  },
  title: {
    color: '#fff',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
  },
  subtitle: {
    color: '#60a5fa',
    fontWeight: 'bold',
    display: 'flex', 
    alignItems: 'center'
  },
  text: {
    color: '#fff'
  },
  textSecondary: {
    color: 'rgba(255, 255, 255, 0.7)'
  },
  textField: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff'
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#60a5fa'
    },
    '& .MuiFormHelperText-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: '0.85rem',
      marginTop: '8px',
      '&.Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.5)'
      }
    },
    '& .MuiInputBase-input::placeholder': {
      color: 'rgba(255, 255, 255, 0.5) !important',
      opacity: 1
    },
    '& .MuiInputBase-input.Mui-disabled::placeholder': {
      color: 'rgba(255, 255, 255, 0.3) !important',
      opacity: 1
    }
  },
  button: {
    borderRadius: '10px',
    padding: '10px 24px',
    fontWeight: 'bold',
    textTransform: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)'
    }
  },
  primaryButton: {
    backgroundColor: '#60a5fa',
    color: '#fff',
    boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
    '&:hover': {
      backgroundColor: '#3b82f6',
      boxShadow: '0 6px 12px rgba(30, 136, 229, 0.4)',
    }
  },
  warningButton: {
    backgroundColor: '#f59e0b',
    color: '#fff',
    boxShadow: '0 4px 10px rgba(245, 158, 11, 0.3)',
    '&:hover': {
      backgroundColor: '#d97706',
      boxShadow: '0 6px 12px rgba(245, 158, 11, 0.4)',
    }
  },
  radio: {
    '&.MuiRadio-root': {
      color: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-checked': {
      color: '#60a5fa',
    }
  },
  radioLabel: {
    color: '#fff'
  },
  menuPaper: {
    backgroundColor: 'rgba(35, 35, 50, 0.95)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px'
  },
  menuItem: {
    color: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    }
  },
  errorText: {
    color: '#f87171'
  },
  apiKeyField: {
    mt: 1,
    "& .MuiInputBase-input": {
      fontWeight: "bold",
      color: "#fff !important",
      fontSize: "1.1rem",
      letterSpacing: "0.5px"
    },
    "& .Mui-disabled": {
      WebkitTextFillColor: "rgba(255, 255, 255, 0.7) !important",
      "-webkit-text-fill-color": "rgba(255, 255, 255, 0.7) !important"
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(35, 35, 50, 0.8)"
    }
  },
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    bgcolor: 'rgba(25, 25, 38, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  ipListBox: {
    mt: 1, 
    p: 2, 
    bgcolor: 'rgba(255, 255, 255, 0.05)', 
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  },
  divider: {
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    mt: 1,
    pt: 1,
  }
};

const My = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [accountNumber, setAccountNumber] = useState('');
  const [currentAmountAlert, setCurrentAmountAlert] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [rtpValue, setRtpValue] = useState('96');
  const [serverIps, setServerIps] = useState('');
  const [allowedIps, setAllowedIps] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 초기 데이터 로드
  useEffect(() => {
    fetchAgentData();
  }, []);

  // 에이전트 데이터 가져오기
  const fetchAgentData = async () => {
    setLoading(true);
    try {
      const user = getUser(); // 로그인한 사용자 정보 가져오기
      console.log('로그인 사용자 정보:', user);
      
      if (!user || !user.id) {
        setError('로그인 정보를 찾을 수 없습니다.');
        return;
      }

      const agent_id = user.id;
      // console.log('요청 URL:', `/info/${agent_id}`);
      
      // axiosInstance 사용 (자동으로 토큰과 baseURL 설정됨)
      const response = await axiosInstance.get(`/agent/info/${agent_id}`);
      console.log('서버 응답:', response.data);
      
      if (response.data.success) {
        setAgentData(response.data.agent);
        if (response.data.agent.rtp) setRtpValue(response.data.agent.rtp.toString());
        if (response.data.agent.account_n) setAccountNumber(response.data.agent.account_n);
        if (response.data.agent.amount_alert) setCurrentAmountAlert(response.data.agent.amount_alert);
        
        // IP 리스트 가져오기
        fetchAllowedIps(agent_id);
      } else {
        setError(response.data.message || '에이전트 정보를 가져오는 데 실패했습니다.');
      }
    } catch (err) {
      console.error('API 호출 에러::', err.response || err);
      setError(err.response?.data?.message || '서버 연결에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };
  
  // 허용된 IP 목록 가져오기
  const fetchAllowedIps = async (agent_id) => {
    try {
      const response = await axiosInstance.get(`/agent/listIps?agent_id=${agent_id}`);
      if (response.data.success) {
        setAllowedIps(response.data.ips || []);
      }
    } catch (err) {
      console.error('IP 목록 가져오기 실패:', err);
    }
  };

  // RTP 설정 저장
  const saveRtpSettings = async () => {
    setLoading(true);
    try {
      // 현재 로그인한 에이전트의 ID 가져오기
      const agent_id = agentData.id; // 또는 getUser().id 등 현재 로그인된 에이전트 ID
  
      // get 요청에서는 모든 데이터를 쿼리 파라미터로 전달
      const response = await axiosInstance.get(`/agent/update?agent_id=${agent_id}&rtp=${rtpValue}`);
      
      if (response.data.success) {
        setAgentData({
          ...agentData,
          rtp: rtpValue
        });
        setSuccess('RTP 설정이 성공적으로 저장되었습니다.');
      } else {
        setError(response.data.message || 'RTP 설정 저장에 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다. 다시 시도해주세요.');
      console.error('Error saving RTP settings:', err);
    } finally {
      setLoading(false);
    }
  };

  // API 키 재발급
  const regenerateApiKey = async () => {
    setLoading(true);
    try {
      const agent_id = agentData?.id;
      if (!agent_id) {
        setError('에이전트 정보를 찾을 수 없습니다.');
        return;
      }
      
      // GET 요청에서는 쿼리 파라미터로 데이터 전달
      const response = await axiosInstance.get(`/agent/update?agent_id=${agent_id}&api_key=true`);
      
      if (response.data.success) {
        setAgentData(response.data.agent);  // 전체 에이전트 객체로 상태 업데이트
        setSuccess('API 키가 성공적으로 재발급되었습니다.');
      } else {
        setError(response.data.message || 'API 키 재발급에 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다. 다시 시도해주세요.');
      console.error('Error regenerating API key:', err);
    } finally {
      setLoading(false);
    }
  };

  // 계좌 번호 변경
  const changeAccountNumber = async () => {
    if (!accountNumber.trim()) {
      setError('계좌번호를 입력해주세요.');
      return;
    }
    
    setLoading(true);
    try {
      const agent_id = agentData?.id;
      if (!agent_id) {
        setError('에이전트 정보를 찾을 수 없습니다.');
        return;
      }
  
      // GET 요청에서는 쿼리 파라미터로 데이터 전달
      const response = await axiosInstance.get(`/agent/update?agent_id=${agent_id}&account_n=${encodeURIComponent(accountNumber)}`);
      
      if (response.data.success) {
        setAgentData({
          ...agentData,
          account_n: accountNumber
        });
        setSuccess('계좌 번호가 성공적으로 변경되었습니다.');
      } else {
        setError(response.data.message || '계좌 번호 변경에 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다. 다시 시도해주세요.');
      console.error('Error changing account number:', err);
    } finally {
      setLoading(false);
    }
  };

  // 보유 금액 알림 설정
  const changeAmountAlert = async () => {
    if (!currentAmountAlert.trim()) {
      setError('알림 금액을 입력해주세요.');
      return;
    }

    // 숫자 형식인지 확인
    if (isNaN(Number(currentAmountAlert))) {
      setError('유효한 숫자를 입력해주세요.');
      return;
    }
    
    setLoading(true);
    try {
      const agent_id = agentData?.id;
      if (!agent_id) {
        setError('에이전트 정보를 찾을 수 없습니다.');
        return;
      }
    
      // GET 요청에서는 쿼리 파라미터로 데이터 전달
      const response = await axiosInstance.get(`/agent/update?agent_id=${agent_id}&amount_alert=${currentAmountAlert}`);
      
      if (response.data.success) {
        setAgentData({
          ...agentData,
          amount_alert: currentAmountAlert
        });
        setSuccess('보유 금액 알림이 성공적으로 설정되었습니다.');
      } else {
        setError(response.data.message || '보유 금액 알림 설정에 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다. 다시 시도해주세요.');
      console.error('Error changing amount alert:', err);
    } finally {
      setLoading(false);
    }
  };

  // 서버 IP 등록
  const registerServerIps = async () => {
    if (!serverIps.trim()) {
      setError('서버 IP를 입력해주세요.');
      return;
    }
    
    setLoading(true);
    try {
      const agent_id = agentData?.id;
      if (!agent_id) {
        setError('에이전트 정보를 찾을 수 없습니다.');
        return;
      }
    
      // IP 등록 요청
      const response = await axiosInstance.get(`/agent/update?agent_id=${agent_id}&ip_address=${encodeURIComponent(serverIps)}`);
      
      if (response.data.success) {
        setSuccess('서버 IP가 성공적으로 등록되었습니다.');
        setServerIps(''); // 입력 필드 초기화
        // IP 목록 새로고침
        fetchAllowedIps(agent_id);
      } else {
        setError(response.data.message || 'IP 등록에 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다. 다시 시도해주세요.');
      console.error('Error registering server IPs:', err);
    } finally {
      setLoading(false);
    }
  };

  // 메뉴 관련 핸들러
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // 메뉴 아이템 클릭 핸들러
  const handleMenuItemClick = (action) => {
    handleMenuClose();
    
    // 각 메뉴 액션에 따른 처리
    if (action === 'edit') {
      // 수정 페이지로 이동하는 로직
      console.log('에이전트 수정');
    } else if (action === 'payment') {
      // 지급 내역 페이지로 이동하는 로직
      console.log('지급 내역');
    } else if (action === 'charge') {
      // 충전 내역 페이지로 이동하는 로직
      console.log('충전 내역');
    } else if (action === 'users') {
      // 유저 목록 페이지로 이동하는 로직
      console.log('유저 목록');
    } else if (action === 'user_payment') {
      // 유저 지급 내역 페이지로 이동하는 로직 
      console.log('유저 지급 내역');
    }
  };

  // 알림 닫기
  const handleCloseAlert = () => {
    setError(null);
    setSuccess(null);
  };

  // IP 주소 마스킹 처리 함수
  const maskIpAddress = (ip) => {
    if (!ip) return '';
    const parts = ip.split('.');
    if (parts.length === 4) {
      return `${parts[0]}.${parts[1]}.***.***`;
    }
    return ip;
  };

  // 로딩 중일 때 화면
  if (loading && !agentData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)'
      }}>
        <CircularProgress sx={{ color: '#60a5fa' }} />
      </Box>
    );
  }

  return (
    <Box sx={darkModeStyles.container}>
      {/* 로딩 오버레이 */}
      {loading && (
        <Box sx={darkModeStyles.loadingOverlay}>
          <CircularProgress sx={{ color: '#60a5fa' }} />
        </Box>
      )}

      {/* 에러 메시지 */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert 
          onClose={handleCloseAlert} 
          severity="error" 
          sx={{ 
            width: '100%',
            backgroundColor: 'rgba(220, 38, 38, 0.9)',
            color: '#fff',
            '& .MuiAlert-icon': {
              color: '#fff'
            } 
          }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* 성공 메시지 */}
      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert 
          onClose={handleCloseAlert} 
          severity="success" 
          sx={{ 
            width: '100%',
            backgroundColor: 'rgba(34, 197, 94, 0.9)',
            color: '#fff',
            '& .MuiAlert-icon': {
              color: '#fff'
            } 
          }}
        >
          {success}
        </Alert>
      </Snackbar>

      {/* 게임 설정 */}
      <Paper elevation={3} sx={darkModeStyles.paper}>
        <Typography
          variant="h6"
          fontWeight="bold"
          gutterBottom
          sx={darkModeStyles.title}
        >
          게임 설정
        </Typography>
        <Typography
          variant="body2"
          gutterBottom
          sx={{ ...darkModeStyles.textSecondary, mb: 2 }}
        >
          프라그마틱 최소 RTP 설정 변경 후 즉시 유저들에게 적용됩니다. <br />
          모든 게임에 대해서 동일한 RTP가 적용되지 않을 수 있습니다.
        </Typography>
        <RadioGroup 
          row 
          sx={{ mb: 2 }}
          value={rtpValue}
          onChange={(e) => setRtpValue(e.target.value)}
        >
          <FormControlLabel 
            value="92" 
            control={<Radio sx={darkModeStyles.radio} />} 
            label="92%" 
            sx={{ color: '#fff' }}
          />
          <FormControlLabel 
            value="94" 
            control={<Radio sx={darkModeStyles.radio} />} 
            label="94%" 
            sx={{ color: '#fff' }}
          />
          <FormControlLabel 
            value="96" 
            control={<Radio sx={darkModeStyles.radio} />} 
            label="96%" 
            sx={{ color: '#fff' }}
          />
        </RadioGroup>
        <Button
          variant="contained"
          onClick={saveRtpSettings}
          disabled={loading}
          sx={{
            ...darkModeStyles.button,
            ...darkModeStyles.warningButton
          }}
        >
          설정 저장
        </Button>
        <Typography variant="body2" sx={{ ...darkModeStyles.errorText, mt: 2 }}>
          주의사항: (RTP) 함수율을 변경할 때 이력 및 베팅 내역과 리플레이도 함께 초기화됩니다. <br />
          유저에게 혼란을 줄 수 있는 점 참고하여 사용하시기 바랍니다.
        </Typography>
      </Paper>

      {/* API 서버 IP 등록 */}
      <Paper elevation={3} sx={darkModeStyles.paper}>
        <Typography
          variant="h6"
          fontWeight="bold"
          gutterBottom
          sx={{
            ...darkModeStyles.subtitle,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          API 서버 IP 등록
          <Tooltip 
            title="외부 API 호출 시 서버 IP를 등록해야 합니다. 다중 IP 등록 시에는 쉼표(,)로 구분해주세요."
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'rgba(35, 35, 50, 0.95)',
                  color: '#fff',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  fontSize: '0.8rem',
                  maxWidth: 300
                }
              }
            }}
          >
            <IconButton size="small" sx={{ ml: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <Typography
          variant="body2"
          gutterBottom
          sx={{ ...darkModeStyles.textSecondary, mb: 2 }}
        >
          외부 API 호출 시 서버 IP를 등록해야 합니다.<br />
          다중 IP를 등록 시에는 쉼표(,)로 구분해주세요.<br />
          등록 시에는 보안을 위해 IP가 가려집니다.<br />
          변경 시 고객센터에 문의해 IP 초기화 요청을 하셔야 합니다.
        </Typography>
        
        {/* IP 등록 입력 필드 - 이미 IP가 등록되어 있으면 비활성화 */}
        <TextField
          fullWidth
          placeholder=""
          value={serverIps}
          onChange={(e) => setServerIps(e.target.value)}
          sx={{
            ...darkModeStyles.textField,
            '& .MuiFormHelperText-root': {
              color: 'rgba(255, 255, 255, 0.7) !important',
              fontSize: '0.85rem !important',
              marginTop: '8px !important',
              opacity: 1,
            },
            '& .MuiFormHelperText-root.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.7) !important',
              opacity: 1,
            }
          }}
          disabled={allowedIps.length > 0}
          helperText={allowedIps.length > 0 ? "IP가 이미 등록되어 있습니다. 변경이 필요하면 고객센터에 문의하세요." : ""}
        />
        <Button
          variant="contained"
          size="large"
          sx={{
            mt: 2,
            ...darkModeStyles.button,
            ...darkModeStyles.primaryButton
          }}
          onClick={registerServerIps}
          disabled={loading || allowedIps.length > 0}
        >
          IP 등록
        </Button>

        {/* 등록된 IP 목록 */}
        {allowedIps.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={darkModeStyles.text}>
              등록된 IP 목록
            </Typography>
            <Box sx={darkModeStyles.ipListBox}>
              {allowedIps.map((ip, index) => (
                <Typography key={index} variant="body2" sx={{ ...darkModeStyles.textSecondary, mb: 1 }}>
                  {maskIpAddress(ip.ip_address)}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Paper>  

      {/* 기본 정보 */}
      <Paper elevation={3} sx={darkModeStyles.paper}>
        <Typography
          variant="h4"
          fontWeight="bold"
          gutterBottom
          sx={{ ...darkModeStyles.title, mb: 3 }}
        >
          기본 정보
        </Typography>
        <Grid container spacing={3}>
          {/* 번호 */}
          <Grid item xs={12} md={6}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>번호:</strong> {agentData?.agent_srl || '13178'}
            </Typography>
          </Grid>

          {/* 분류 */}
          <Grid item xs={12} md={6}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>분류:</strong> {agentData?.category || '영업1'}
            </Typography>
          </Grid>

          {/* 타입 */}
          <Grid item xs={12} md={6}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>타입:</strong> {agentData?.type || '병행'}
            </Typography>
          </Grid>

          {/* 소속 에이전트 */}
          <Grid item xs={12} md={6}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>상위 에이전트:</strong>{' '}
              <Button
                disabled
                variant="contained"
                sx={{
                  cursor: 'default',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  '&.Mui-disabled': {
                    color: '#fff',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {agentData?.parents_agent}
              </Button>
            </Typography>
          </Grid>

          {/* 에이전트 */}
          <Grid item xs={12}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>에이전트:</strong>{' '}
              <Button
                variant="outlined"
                sx={{ 
                  cursor: 'pointer',
                  color: '#60a5fa',
                  borderColor: 'rgba(96, 165, 250, 0.5)',
                  '&:hover': {
                    borderColor: '#60a5fa',
                    backgroundColor: 'rgba(96, 165, 250, 0.1)'
                  }
                }}
                onClick={handleMenuOpen}
              >
                {agentData?.id}
              </Button>
            </Typography>

            {/* Dropdown Menu */}
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              PaperProps={{
                sx: darkModeStyles.menuPaper,
                style: { width: '200px' }
              }}
            >
              <MenuItem onClick={() => handleMenuItemClick('edit')} sx={darkModeStyles.menuItem}>수정</MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('payment')} sx={darkModeStyles.menuItem}>지급 내역</MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('charge')} sx={darkModeStyles.menuItem}>충전 내역</MenuItem>
              <Box sx={darkModeStyles.divider}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    pl: 2,
                    color: '#60a5fa',
                    fontWeight: 'bold',
                  }}
                >
                  유저
                </Typography>
                <MenuItem onClick={() => handleMenuItemClick('users')} sx={darkModeStyles.menuItem}>유저 목록</MenuItem>
                <MenuItem onClick={() => handleMenuItemClick('user_payment')} sx={darkModeStyles.menuItem}>지급 내역</MenuItem>
              </Box>
            </Menu>
          </Grid>

          {/* API KEY */}
          <Grid item xs={12}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>API KEY:</strong>
            </Typography>
            <TextField
              fullWidth
              placeholder="API KEY"
              value={agentData?.api_key || "mKFdbM..."}
              disabled
              InputProps={{
                readOnly: true,
              }}
              sx={darkModeStyles.apiKeyField}
            />
            <Typography
              variant="caption"
              sx={{ mt: 1, display: 'block', color: '#f87171' }}
            >
              API KEY는 개발에 필요한 정보입니다. 개발자에게 전달해주세요.
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                mt: 2,
                ...darkModeStyles.button,
                ...darkModeStyles.primaryButton
              }}
              onClick={regenerateApiKey}
              disabled={loading}
            >
              재발급
            </Button>
          </Grid>

          {/* 계좌 번호 */}
          <Grid item xs={12}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>계좌 번호</strong>
            </Typography>
            <TextField
              fullWidth
              placeholder="계좌 번호를 입력하세요"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              sx={{ ...darkModeStyles.textField, mt: 1 }}
            />
            <Button
              variant="contained"
              size="large"
              sx={{
                mt: 2,
                ...darkModeStyles.button,
                ...darkModeStyles.primaryButton
              }}
              onClick={changeAccountNumber}
              disabled={loading}
            >
              변경
            </Button>
          </Grid>

          {/* 현재 보유 금액 알림 */}
          <Grid item xs={12}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>현재 보유 금액 알림</strong>
            </Typography>
            <TextField
              fullWidth
              placeholder="보유 금액 알림 설정"
              value={currentAmountAlert}
              onChange={(e) => {
                // 숫자만 입력 가능하도록
                const onlyNums = e.target.value.replace(/[^0-9]/g, '');
                setCurrentAmountAlert(onlyNums);
              }}
              sx={{ ...darkModeStyles.textField, mt: 1 }}
              type="number"
              InputProps={{
                inputProps: { min: 0 }
              }}
            />
            <Typography
              variant="caption"
              sx={{ mt: 1, display: 'block', color: '#f87171' }}
            >
              현재 보유 금액이 설정한 금액 이하로 떨어지면 알림을 받을 수 있습니다.
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                mt: 2,
                ...darkModeStyles.button,
                ...darkModeStyles.primaryButton
              }}
              onClick={changeAmountAlert}
              disabled={loading}
            >
              변경
            </Button>
          </Grid>

          {/* 컷포인트 */}
          <Grid item xs={12} md={6}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>보유 포인트:</strong> {agentData?.cur_point ? String(agentData.cur_point).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 'N/A'}
            </Typography>
          </Grid>

          {/* 수수료 */}
          <Grid item xs={12} md={6}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>수수료:</strong> {agentData?.fee || 'N/A'}
            </Typography>
          </Grid>

          {/* 총 수금 */}
          <Grid item xs={12} md={6}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>총 수금:</strong> {agentData?.total_withdraw ? String(agentData.total_withdraw).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '0'}
            </Typography>
          </Grid>

          {/* 총 지급 */}
          <Grid item xs={12} md={6}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>총 지급:</strong> {agentData?.total_deposit ? String(agentData.total_deposit).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '0'}
            </Typography>
          </Grid>

          {/* 총 충전 */}
          <Grid item xs={12} md={6}>
            <Typography variant="body1" sx={darkModeStyles.text}>
              <strong>총 충전:</strong> {agentData?.total_charge ? String(agentData.total_charge).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '0'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default My;